import React from 'react'
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd'
import { StrictModeDroppable } from 'services/utils/dragDropUtils'
import CustomFieldEditor from './CustomFieldEditor';
import { CustomFieldGroupType, CustomFieldType, LinkedCustomFieldObjectType } from './CustomFieldTypes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import TextInput from '../basefields/TextInput';
import TextareaInput from '../basefields/TextareaInput';
import Dropdown from '../basefields/Dropdown';

export interface CustomFieldGroupEditorProps {
    group: CustomFieldGroupType;
    fields: CustomFieldType[];
    setFields: React.Dispatch<React.SetStateAction<CustomFieldType[]>>;
    setGroups: React.Dispatch<React.SetStateAction<CustomFieldGroupType[]>>;
    onAddField: (groupId?: number | string | null, parentFieldId?: number | string | null) => void;
    selectedLinkedObjectType?: LinkedCustomFieldObjectType;
    allowExtendedOptions: boolean;
    extendedOptionsFeature?: string;
    dragHandleProps?: any;
    showFieldPreview?: boolean;
};

const CustomFieldGroupEditor: React.FC<CustomFieldGroupEditorProps> = ({
    group,
    fields,
    setFields,
    setGroups,
    onAddField,
    selectedLinkedObjectType,
    allowExtendedOptions,
    extendedOptionsFeature,
    dragHandleProps,
    showFieldPreview
}) => {
    const { t } = useTranslation();

    // Get the indentifier for the group
    const groupIdentifier = group.id || group.uuid;

    // Reorder the fields
    const reorderFields = (fieldsToReorder: CustomFieldType[]) => {
        return fieldsToReorder.map((custom_field, index) => ({
            ...custom_field,
            ordering: index + 1,
        }));
    };

    // Update the group
    const updateGroup = (updates: Partial<CustomFieldGroupType>) => {
        setGroups(current => current.map(custom_field_group =>
            (custom_field_group.id || custom_field_group.uuid) === groupIdentifier
                ? { ...custom_field_group, ...updates }
                : custom_field_group
            )
        )
    };

    // Filter the group fields and order them
    const groupFields = fields
        .filter(custom_field =>
            custom_field.group_id === groupIdentifier &&
            !custom_field.parent_field_id &&
            !custom_field.deleted
        )
        .sort((firstField, secondField) => firstField.ordering - secondField.ordering);

    // Determine to show the pdf width setting
    const showPdfWidthSetting = selectedLinkedObjectType === 'form' || selectedLinkedObjectType === 'form_template';

    // Set the pdf width options
    const pdfWidthOptions = [
        { name: t('custom_field.general.group_pdf_width_full_width'), value: 'full_width' },
        { name: t('custom_field.general.group_pdf_width_half_width'), value: 'half_width' },
    ];
    
    return (
        <div className='custom-field-group'>
            <div {...dragHandleProps}
                 className='group-header'>

                {/* Header */}
                <div className='custom-field-group-header'>
                    <div>
                        <h3>
                            {group.title || t('custom_field.general.new_group_label')}
                        </h3>
                        <p>
                            {group.description}
                        </p>
                    </div>
                    <div className='right-items'>
                        <div className='delete-icon tooltip-icon'>
                            <FontAwesomeIcon icon={faTrash} onClick={() => updateGroup({ deleted: true })} />
                            <span className="tooltip">{t('general.delete')}</span>
                        </div>
                        <div className={`order-icon tooltip-icon ${fields.length === 1 ? 'visibility-hidden' : ''}`}>
                            <FontAwesomeIcon icon={faGripLines} />
                            <span className="tooltip">{t('general.reorder')}</span>
                        </div>
                    </div>
                </div>

                {/* Settings */}
                <div className='custom-field-group-row'>
                    <div className={showPdfWidthSetting ? 'show-pdf-width-setting' : ''}>
                        <div className='form-group'>
                            <label>
                                {t('custom_field.general.group_title_label')}
                            </label>
                            <TextInput
                                name='title'
                                value={group.title}
                                placeholder='custom_field.general.group_title_placeholder'
                                onChange={(value) => {
                                    updateGroup({
                                        title: value,
                                    });
                                }}
                            />
                        </div>
                        {showPdfWidthSetting && (
                            <div className='form-group'>
                                <label>
                                    {t('custom_field.general.group_pdf_width_label')}
                                </label>
                                <Dropdown<any>
                                    name='pdf_width'
                                    options={pdfWidthOptions}
                                    selectedOption={
                                        pdfWidthOptions.find(option => option.value === (group.pdf_width || 'full_width')) ?? null
                                    }
                                    optionFormat='name'
                                    selectionFormat={(option) => option.name}
                                    allowNoneOption={false}
                                    showSearch={false}
                                    onChange={(value) => {
                                        updateGroup({
                                            pdf_width: value as CustomFieldGroupType['pdf_width'],
                                        });
                                    }}
                                />
                            </div>
                        )}
                    </div>
                    <div className='form-group'>
                        <label>
                            {t('custom_field.general.group_description_label')}
                        </label>
                        <TextareaInput
                            name='description'
                            value={group.description || ''}
                            placeholder='custom_field.general.group_description_placeholder'
                            rows={2}
                            onChange={(value) => {
                                updateGroup({
                                    description: typeof value === 'string' ? value : value.target.value,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>

            <DragDropContext
                onDragEnd={(result: DropResult) => {
                    if (result.destination) {
                        const reorderedFields = [...groupFields]
                        const [removedField] = reorderedFields.splice(result.source.index, 1)
                        reorderedFields.splice(result.destination.index, 0, removedField)

                        const updatedFields = reorderFields(reorderedFields);

                        setFields(current => {
                            const otherFields = current.filter(custom_field =>
                                custom_field.group_id !== groupIdentifier ||
                                custom_field.parent_field_id
                            )

                            return [ ...otherFields, ...updatedFields]
                        })
                    }
                }}
            >
                <StrictModeDroppable droppableId={`group-${groupIdentifier}`}>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className='grouped-custom-fields'
                        >
                            {groupFields.map((field, index) => (
                                <Draggable
                                    key={field.id || field.uuid}
                                    draggableId={`field-${field.id || field.uuid}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <CustomFieldEditor
                                                field={field}
                                                fields={fields}
                                                setFields={setFields}
                                                selectedLinkedObjectType={selectedLinkedObjectType}
                                                showFieldPreview={showFieldPreview}
                                                allowExtendedOptions={allowExtendedOptions}
                                                extendedOptionsFeature={extendedOptionsFeature}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            <div onClick={(e) => {e.preventDefault(); onAddField(groupIdentifier); }} 
                 className="add-new-button">
                {t('custom_field.general.add_field_to_section_label')}
            </div>
        </div>
    )
}

export default CustomFieldGroupEditor